import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { dateToISOFormat } from '../../../utils';
import { UXPContext } from '@core/context/Context';
import useToast from '@notifications/Toast/useToast';
/**
 * A component to show time series based trend or line visualizations
 * @export
 *
 *
 * @example
 * ```
 *  const TrendData: ITrendSeries[] = [
 *      {
 *          unit: "A",
 *          lineColor: "#ff7300",
            data: [
                { time: "2020/07/20", value: 200 },
                { time: "2020/08/20", value: 100 },
                { time: "2020/09/20", value: 50 },
                { time: "2020/10/20", value: 300 },
                { time: "2020/11/20", value: 700 },
                { time: "2020/12/20", value: 90 }
            ],
            type: "line"
        },
        {
            unit: "B",
            lineColor: "#413ea0",
            fillColor: "#8884d8",
            data: [
                { time: "2014/06/21", value: 50 },
                { time: "2020/07/20", value: 50 },
                { time: "2020/08/20", value: 30 },
                { time: "2020/09/20", value: 90 },
                { time: "2020/10/20", value: 300 },
                { time: "2020/11/20", value: 100 },
                { time: "2020/12/20", value: 90 }
            ],
            type: "area"
        }
    ]
 *
 *  <TrendChartComponent
 *      data={TrendData}
 *  />
 * ```
 */
const TrendChartComponent = (props) => {
    const context = React.useContext(UXPContext);
    let { data, onShowTooltip, onClick, className = '' } = props;
    let Toast = useToast();
    let hasErrors = false;
    React.useEffect(() => {
        if (hasErrors) {
            Toast.error({
                title: context.$L('uxp-core.trend-chart.error-in-chart'),
                content: context.$L('uxp-core.trend-chart.error-description'),
                autoClose: false
            });
        }
    }, [hasErrors]);
    // Get chart colors from CSS variables (theme colors)
    const getChartColor = (index) => {
        const colorIndex = (index % 15) + 1; // Cycle through chartColor1-15
        return `var(--chartColor${colorIndex})`;
    };
    // prepare dataset
    let DataSet = [];
    let Units = [];
    data.map((trend, seriesIndex) => {
        let { unit, lineColor, fillColor, data, type } = trend;
        // Auto-assign colors from theme if not provided
        const assignedLineColor = lineColor || getChartColor(seriesIndex);
        const assignedFillColor = fillColor || getChartColor(seriesIndex);
        if (Units.find((_unit) => (_unit.name == unit)) == undefined) {
            Units.push({ name: unit, lineColor: assignedLineColor, fillColor: assignedFillColor, type: type });
        }
        data.map((trendData) => {
            // get instance
            let _time = trendData.time;
            if (typeof trendData.time == "object") {
                _time = dateToISOFormat(trendData.time);
            }
            let dataset = DataSet.find((_dataset) => _dataset.name == _time);
            if (dataset == undefined) {
                dataset = {
                    'name': _time,
                    [unit]: trendData.value
                };
                DataSet.push(dataset);
            }
            else {
                dataset[unit] = trendData.value;
            }
        });
    });
    DataSet.sort((a, b) => {
        let _date_a = new Date(a.name);
        let _date_b = new Date(b.name);
        if (isNaN(_date_a.getTime()) || isNaN(_date_b.getTime())) {
            console.log("Please format dates to ISO format 'yyyy/mm/dd' : given - " + a.name);
            hasErrors = true;
        }
        else {
            if (_date_a.getTime() < _date_b.getTime())
                return -1;
            if (_date_a.getTime() > _date_b.getTime())
                return 1;
        }
        return 0;
    });
    const customToolTip = (data) => {
        return onShowTooltip(data);
    };
    const onClickHandler = (data) => {
        if (onClick) {
            onClick(data);
        }
    };
    return (_jsx("div", { className: `uxpcore_trendchart ${className}`, children: _jsx(ResponsiveContainer, { className: 'uxpcore_chart__responsive-container', children: _jsxs(ComposedChart, { data: DataSet, children: [onShowTooltip ?
                        _jsx(Tooltip, { content: customToolTip })
                        :
                            _jsx(Tooltip, {}), _jsx(Legend, {}), _jsx(XAxis, { dataKey: "name" }), Units.map((unit, key) => {
                        return _jsx(YAxis, { yAxisId: unit.name }, "y-" + key);
                    }), Units.map((unit, key) => {
                        if (unit.type == "line") {
                            return _jsx(Line, { type: "monotone", dataKey: unit.name, stroke: unit.lineColor, yAxisId: unit.name, onClick: onClickHandler }, key);
                        }
                        else {
                            return _jsx(Area, { type: "monotone", dataKey: unit.name, fill: unit.fillColor, stroke: unit.lineColor, yAxisId: unit.name, onClick: onClickHandler }, key);
                        }
                    })] }) }) }));
};
export default TrendChartComponent;
